/**
 * @file components/shared/HelpCenterModal.jsx
 * @description Help Center modal with FAQ, contact email, and support form.
 */

import { HelpCircle, Mail, MessageSquare, X, ExternalLink, ChevronRight } from "lucide-react";
import { useState } from "react";

const SUPPORT_EMAIL = "yh4now@gmail.com";

const FAQ_ITEMS = [
  {
    q: "How does AI meal tracking work?",
    a: "Simply type what you ate (e.g., '2 eggs and toast') and our AI estimates calories and macros. Premium users get unlimited analyses.",
  },
  {
    q: "How do I cancel my subscription?",
    a: "Go to Menu → Manage Subscription to access your billing portal. You can cancel anytime and keep access until the end of your billing period.",
  },
  {
    q: "Is my data private?",
    a: "Yes. Your data is stored securely in Firebase and only accessible with your account credentials. We never share or sell your data.",
  },
  {
    q: "How accurate are the calorie estimates?",
    a: "Our AI provides estimates within 10-15% accuracy for most common foods. For precise tracking, you can edit any entry manually.",
  },
  {
    q: "Can I use the app offline?",
    a: "Yes! DietReg is a PWA. Previously loaded data is available offline. New entries will sync when you're back online.",
  },
];

export function HelpCenterModal({ onClose }) {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  function handleSendEmail() {
    const mailtoUrl = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject || "DietReg Support")}&body=${encodeURIComponent(message)}`;
    window.open(mailtoUrl, "_blank");
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg max-h-[85vh] bg-brand-surface border border-white/10 rounded-3xl overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-blue-500/10">
              <HelpCircle size={20} className="text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Help & Support</h2>
              <p className="text-[10px] text-gray-500 uppercase tracking-widest">We're here to help</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-white/5 text-gray-400 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Contact section */}
          <div className="p-4 rounded-2xl bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20">
            <div className="flex items-center gap-3 mb-3">
              <Mail size={16} className="text-blue-400" />
              <span className="text-sm font-bold text-white">Contact Us</span>
            </div>
            <p className="text-xs text-gray-400 mb-3">
              Have a question, issue, or feedback? Reach out and we'll get back to you.
            </p>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-500/20 text-blue-300 text-xs font-bold hover:bg-blue-500/30 transition-colors"
            >
              <Mail size={12} />
              {SUPPORT_EMAIL}
              <ExternalLink size={10} />
            </a>
          </div>

          {/* Quick contact form */}
          {!showContactForm ? (
            <button
              onClick={() => setShowContactForm(true)}
              className="w-full p-4 rounded-2xl border border-dashed border-white/10 bg-white/[0.02] flex items-center justify-center gap-3 hover:border-blue-500/30 hover:bg-blue-500/5 transition-all group"
            >
              <MessageSquare size={16} className="text-gray-500 group-hover:text-blue-400 transition-colors" />
              <span className="text-xs text-gray-500 group-hover:text-gray-300 font-medium transition-colors">
                Send us a message
              </span>
            </button>
          ) : (
            <div className="space-y-3 p-4 rounded-2xl border border-white/10 bg-white/[0.02]">
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject (optional)"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500/40"
              />
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Describe your issue or feedback..."
                rows={4}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500/40 resize-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSendEmail}
                  disabled={!message.trim()}
                  className="flex-1 py-3 rounded-xl bg-blue-500 hover:bg-blue-400 disabled:opacity-40 text-white text-xs font-bold uppercase tracking-widest transition-all"
                >
                  Open in Email
                </button>
                <button
                  onClick={() => setShowContactForm(false)}
                  className="px-4 py-3 rounded-xl bg-white/5 text-gray-400 text-xs font-bold hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* FAQ Section */}
          <div>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
              Frequently Asked Questions
            </h3>
            <div className="space-y-2">
              {FAQ_ITEMS.map((item, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-white/5 overflow-hidden bg-white/[0.02]"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-4 text-left"
                  >
                    <span className="text-sm text-gray-200 font-medium pr-4">{item.q}</span>
                    <ChevronRight
                      size={14}
                      className={`text-gray-600 flex-shrink-0 transition-transform ${expandedFaq === i ? "rotate-90" : ""}`}
                    />
                  </button>
                  {expandedFaq === i && (
                    <div className="px-4 pb-4 pt-0 animate-in slide-in-from-top-1 duration-200">
                      <p className="text-xs text-gray-400 leading-relaxed">{item.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/5 bg-white/[0.02]">
          <p className="text-[10px] text-gray-600 text-center">
            DietReg — Made with care. Response time: usually within 24 hours.
          </p>
        </div>
      </div>
    </div>
  );
}
